/* ============================================================
   tz-pdp.js — PDP storefront interactions.
   Loaded ONLY on the product template (theme.liquid conditional).

   Responsibilities:
     1. Gallery — thumb→track scroll, track-scroll → dots/counter/thumbs sync
     2. Variant pickers — option-grid clicks → variant selection,
        price/save-badge/ATC-label/stock-line/sticky-price updates
        + active-state mirror + URL ?variant=… update
     3. AJAX cart-add — submit Buy-Box form → /cart/add.js,
        then open Cart-Drawer (existing tz-shop.js)
     4. Mobile sticky ATC — IntersectionObserver on main ATC
     5. Share button — navigator.share with clipboard fallback

   Dependencies: tz-shop.js (uses its drawer-open API via
   document.dispatchEvent(new CustomEvent('tz:open-drawer', { detail: 'cart' })))
   ============================================================ */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────
     Bootstrap — read product JSON from inline script tag.
     The PDP main section emits <script id="tz-pdp-product" type="application/json">{...}</script>
     with the variants list. If absent, JS still degrades gracefully.
     ────────────────────────────────────────────────────── */
  function readProduct() {
    var el = document.getElementById('tz-pdp-product');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  var PRODUCT = readProduct();

  function formatMoney(cents, currency) {
    var n = (cents / 100).toFixed(2).replace('.', ',');
    return n + ' ' + (currency || '€');
  }

  /* ──────────────────────────────────────────────────────
     GALLERY
     ────────────────────────────────────────────────────── */
  function initGallery(root) {
    var track = root.querySelector('[data-pdp-gallery-track]');
    var thumbs = root.querySelectorAll('.tz-pdp-gallery__thumb');
    var dots = root.querySelectorAll('.tz-pdp-gallery__dot');
    var counter = root.querySelector('[data-pdp-gallery-counter]');
    if (!track) return;

    function setActive(idx) {
      var slides = track.children;
      if (idx < 0 || idx >= slides.length) return;
      track.scrollTo({ left: slides[idx].offsetLeft, behavior: 'smooth' });
      thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === idx); });
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
      if (counter) counter.textContent = (idx + 1) + ' / ' + slides.length;
    }
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { setActive(i); });
    });

    initLightbox(track, setActive);

    // Sync from scroll (mobile swipe)
    var scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var w = track.clientWidth;
        if (!w) return;
        var i = Math.round(track.scrollLeft / w);
        thumbs.forEach(function (t, j) { t.classList.toggle('is-active', j === i); });
        dots.forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
        if (counter) counter.textContent = (i + 1) + ' / ' + track.children.length;
      }, 80);
    });
  }


  /* ──────────────────────────────────────────────────────
     LIGHTBOX v2 — tap/click a gallery image to view fullscreen.
     Additive & self-contained: builds its own <dialog> from the
     existing slides on first open; degrades silently without
     <dialog>. High-res via Shopify CDN width param.
     Zoom engine (transform-based, like native photo viewers):
       • Mobile: continuous two-finger pinch (1×–4×), one-finger
         pan while zoomed, double-tap toggles 1× ↔ 2×.
       • Desktop: single click toggles 1× ↔ 2× at cursor, drag to
         pan while zoomed, mouse wheel zooms smoothly.
     While not zoomed, horizontal swipe switches slides (native
     scroll-snap); zoom locks the slide until reset.
     ────────────────────────────────────────────────────── */
  function initLightbox(track, syncActive) {
    if (typeof HTMLDialogElement === 'undefined') return;
    var srcImgs = [];
    Array.prototype.forEach.call(track.children, function (s) {
      var im = s.querySelector('img');
      if (im) srcImgs.push(im);
    });
    if (!srcImgs.length) return;

    var dlg = null, lbTrack = null, lbCounter = null, zoomers = [];

    function hiRes(im) {
      var src = im.currentSrc || im.src || '';
      return src.replace(/([?&]width=)\d+/, '$1' + 2048);
    }
    function current() {
      if (!lbTrack || !lbTrack.clientWidth) return 0;
      return Math.round(lbTrack.scrollLeft / lbTrack.clientWidth);
    }
    function updateCounter() {
      if (lbCounter) lbCounter.textContent = (current() + 1) + ' / ' + srcImgs.length;
    }
    function unzoomAll() {
      zoomers.forEach(function (z) { z.reset(false); });
    }

    function makeZoomer(pan) {
      var img = pan.querySelector('img');
      var MAXS = 4, CLICKS = 2;
      var scale = 1, tx = 0, ty = 0;

      function apply(animate) {
        img.style.transition = animate ? 'transform 0.25s ease' : 'none';
        img.style.transform = 'translate(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px) scale(' + scale.toFixed(4) + ')';
        pan.classList.toggle('is-zoom', scale > 1.01);
      }
      function clampPan() {
        var r = pan.getBoundingClientRect();
        var mx = Math.max(0, (img.offsetWidth * scale - r.width) / 2);
        var my = Math.max(0, (img.offsetHeight * scale - r.height) / 2);
        tx = Math.min(mx, Math.max(-mx, tx));
        ty = Math.min(my, Math.max(-my, ty));
      }
      function zoomTo(s, cx, cy, animate) {
        s = Math.max(1, Math.min(MAXS, s));
        var r = pan.getBoundingClientRect();
        var mx = cx - (r.left + r.width / 2);
        var my = cy - (r.top + r.height / 2);
        tx = mx - (mx - tx) * (s / scale);
        ty = my - (my - ty) * (s / scale);
        scale = s;
        if (scale <= 1.01) { scale = 1; tx = 0; ty = 0; }
        clampPan(); apply(animate);
      }
      function reset(animate) { scale = 1; tx = 0; ty = 0; apply(animate); }
      function isZoomed() { return scale > 1.01; }

      // ── Pointer-Gesten: Pinch (2 Finger), Pan (1 Finger/Maus-Drag)
      var pts = new Map(), startDist = 0, startScale = 1, lastMid = null, moved = false, downX = 0, downY = 0;
      var overX = 0, baseScroll = 0, handing = false;
      var gestureT = 0;
      function vals() { return Array.from(pts.values()); }
      function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y) || 1; }
      function mid(a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }

      pan.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try { pan.setPointerCapture(e.pointerId); } catch (err) {}
        if (pts.size === 1) {
          moved = false; downX = e.clientX; downY = e.clientY;
          overX = 0; baseScroll = lbTrack.scrollLeft; handing = false;
          gestureT = Date.now();
        }
        if (pts.size === 2) {
          if (handing) {
            lbTrack.style.scrollSnapType = '';
            lbTrack.scrollLeft = baseScroll;
            overX = 0; handing = false;
          }
          var a = vals();
          startDist = dist(a[0], a[1]);
          startScale = scale;
          lastMid = mid(a[0], a[1]);
          moved = true;
        }
      });
      pan.addEventListener('pointermove', function (e) {
        if (!pts.has(e.pointerId)) return;
        var prev = pts.get(e.pointerId);
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (Math.abs(e.clientX - downX) > 6 || Math.abs(e.clientY - downY) > 6) moved = true;
        if (pts.size === 2) {
          var a = vals();
          var m = mid(a[0], a[1]);
          tx += m.x - lastMid.x; ty += m.y - lastMid.y;
          zoomTo(startScale * dist(a[0], a[1]) / startDist, m.x, m.y, false);
          lastMid = m;
        } else if (pts.size === 1) {
          var dx = e.clientX - prev.x;
          var dy = e.clientY - prev.y;
          // Rückwärtsziehen baut erst den Slide-Überhang ab
          if (overX !== 0 && ((overX < 0 && dx > 0) || (overX > 0 && dx < 0))) {
            var unwind = Math.min(Math.abs(dx), Math.abs(overX)) * (dx > 0 ? 1 : -1);
            overX += unwind; dx -= unwind;
          }
          if (dx !== 0) {
            var r = pan.getBoundingClientRect();
            var mx = Math.max(0, (img.offsetWidth * scale - r.width) / 2);
            var desired = tx + dx;
            var clamped = Math.min(mx, Math.max(-mx, desired));
            overX += desired - clamped;
            tx = clamped;
          }
          ty += dy;
          clampPan(); apply(false);
          // Am Bildrand weiterwischen → Track zieht zum Nachbarbild
          if (overX !== 0 || handing) {
            if (!handing) { handing = true; lbTrack.style.scrollSnapType = 'none'; }
            lbTrack.scrollLeft = baseScroll - overX;
          }
        }
      });
      function lift(e) {
        pts.delete(e.pointerId);
        if (pts.size < 2) { startDist = 0; lastMid = null; }
        if (pts.size === 1) { var a = vals()[0]; downX = a.x; downY = a.y; }
        if (pts.size === 0 && !handing && scale > 1 && scale < 1.3) {
          reset(true);
        }
        if (pts.size === 0 && handing) {
          var W = lbTrack.clientWidth;
          var threshold = Math.min(90, W * 0.2);
          var flick = (Date.now() - gestureT) < 300 && Math.abs(overX) > 35;
          lbTrack.style.scrollSnapType = '';
          if (Math.abs(overX) > threshold || flick) {
            var idx = Math.round(baseScroll / W) + (overX < 0 ? 1 : -1);
            idx = Math.max(0, Math.min(srcImgs.length - 1, idx));
            lbTrack.scrollTo({ left: idx * W, behavior: 'smooth' });
          } else {
            lbTrack.scrollTo({ left: baseScroll, behavior: 'smooth' });
          }
          handing = false; overX = 0;
          if (scale > 1 && scale < 1.3) reset(true);
        }
      }
      pan.addEventListener('pointerup', lift);
      pan.addEventListener('pointercancel', lift);
      // Browser-Scroll bei 2 Fingern unterbinden, damit der Pinch bei uns ankommt
      pan.addEventListener('touchmove', function (e) {
        if (e.touches.length >= 2) e.preventDefault();
      }, { passive: false });

      // ── Desktop: Einfachklick zoomt rein/raus (nur ohne Drag), Rad zoomt stufenlos
      pan.addEventListener('click', function (e) {
        if (moved) return;
        // Pointer-Capture retargetet aufs Pan — daher Hit-Test über Koordinaten
        var r = img.getBoundingClientRect();
        var onImg = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!onImg) {
          if (isZoomed()) { reset(true); } else if (dlg) { dlg.close(); }
          return;
        }
        if (e.pointerType === 'touch' || (e.sourceCapabilities && e.sourceCapabilities.firesTouchEvents)) return;
        if (isZoomed()) { reset(true); } else { zoomTo(CLICKS, e.clientX, e.clientY, true); }
      });
      pan.addEventListener('wheel', function (e) {
        e.preventDefault();
        zoomTo(scale * (e.deltaY < 0 ? 1.18 : 1 / 1.18), e.clientX, e.clientY, false);
      }, { passive: false });

      // ── Mobile: Doppeltipp 1× ↔ 2×
      var lastTap = 0, lastX = 0, lastY = 0;
      pan.addEventListener('touchend', function (e) {
        if (e.touches.length) return;
        var t = e.changedTouches[0];
        var now = Date.now();
        if (now - lastTap < 320 && Math.abs(t.clientX - lastX) < 40 && Math.abs(t.clientY - lastY) < 40) {
          e.preventDefault();
          if (isZoomed()) { reset(true); } else { zoomTo(CLICKS, t.clientX, t.clientY, true); }
          lastTap = 0;
        } else {
          lastTap = now; lastX = t.clientX; lastY = t.clientY;
        }
      });

      return { reset: reset, isZoomed: isZoomed };
    }

    function build() {
      dlg = document.createElement('dialog');
      dlg.className = 'tz-lb';
      dlg.setAttribute('aria-label', 'Produktbilder — Vollbild');
      var html = '<div class="tz-lb__head"><span class="tz-lb__counter" data-tz-lb-counter></span>' +
        '<button type="button" class="tz-lb__close" aria-label="Schließen">×</button></div>' +
        '<div class="tz-lb__track" data-tz-lb-track>';
      srcImgs.forEach(function (im) {
        var alt = (im.getAttribute('alt') || '').replace(/"/g, '&quot;');
        html += '<div class="tz-lb__slide"><div class="tz-lb__pan"><img src="' + hiRes(im) +
          '" alt="' + alt + '" loading="lazy" draggable="false"></div></div>';
      });
      html += '</div>' +
        '<button type="button" class="tz-lb__arrow tz-lb__arrow--prev" aria-label="Vorheriges Bild">‹</button>' +
        '<button type="button" class="tz-lb__arrow tz-lb__arrow--next" aria-label="Nächstes Bild">›</button>';
      dlg.innerHTML = html;
      document.body.appendChild(dlg);
      lbTrack = dlg.querySelector('[data-tz-lb-track]');
      lbCounter = dlg.querySelector('[data-tz-lb-counter]');
      dlg.querySelectorAll('.tz-lb__pan').forEach(function (pan) {
        zoomers.push(makeZoomer(pan));
      });

      dlg.querySelector('.tz-lb__close').addEventListener('click', function () { dlg.close(); });
      dlg.addEventListener('close', function () {
        document.documentElement.classList.remove('tz-lb-open');
        unzoomAll();
        if (syncActive) syncActive(current());
      });
      // Klick auf dunklen Rand (nicht aufs Bild) schließt
      dlg.addEventListener('click', function (e) {
        if (e.target === dlg || e.target.classList.contains('tz-lb__slide')) dlg.close();
      });
      dlg.querySelector('.tz-lb__arrow--prev').addEventListener('click', function () { go(-1); });
      dlg.querySelector('.tz-lb__arrow--next').addEventListener('click', function () { go(1); });
      dlg.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      });

      var scrollTimer, lastIdx = 0;
      lbTrack.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var i = current();
          if (i !== lastIdx) { unzoomAll(); lastIdx = i; }
          updateCounter();
        }, 80);
      });
    }

    function go(dir) {
      var i = Math.max(0, Math.min(srcImgs.length - 1, current() + dir));
      lbTrack.scrollTo({ left: i * lbTrack.clientWidth, behavior: 'smooth' });
    }

    function openAt(idx) {
      if (!dlg) build();
      document.documentElement.classList.add('tz-lb-open');
      dlg.showModal();
      unzoomAll();
      lbTrack.scrollLeft = idx * lbTrack.clientWidth;
      updateCounter();
    }

    srcImgs.forEach(function (im, i) {
      im.addEventListener('click', function () { openAt(i); });
      im.classList.add('tz-lb-trigger');
    });
  }

  /* ──────────────────────────────────────────────────────
     VARIANT PICKERS
     Each .tz-pdp-variant has data-option-index="N".
     Clicks read data-value, then we find the matching variant
     by combining selected values across all option groups.
     ────────────────────────────────────────────────────── */
  function getSelectedValues(form) {
    var values = [];
    form.querySelectorAll('[data-tz-variant-group]').forEach(function (group) {
      var idx = parseInt(group.dataset.optionIndex, 10);
      var active = group.querySelector('.tz-pdp-variant__opt.is-active');
      if (active) values[idx] = active.dataset.value;
    });
    return values;
  }
  function findVariant(values) {
    if (!PRODUCT || !PRODUCT.variants) return null;
    return PRODUCT.variants.find(function (v) {
      // v.options is an array of strings positionally matching product.options
      return v.options.every(function (val, i) { return val === values[i]; });
    });
  }

  function applyVariant(form, variant) {
    if (!variant) return;
    var currency = PRODUCT && PRODUCT.currency ? PRODUCT.currency : '€';

    // Hidden ID input
    var idInput = form.querySelector('[data-tz-variant-id]');
    if (idInput) idInput.value = variant.id;

    // Price + compare-at + save badge — update ALL copies (buybox + mobile head)
    var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    var pct = onSale ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
    document.querySelectorAll('[data-tz-price]').forEach(function (el) { el.textContent = formatMoney(variant.price, currency); });
    document.querySelectorAll('[data-tz-price-was]').forEach(function (el) {
      if (onSale) { el.textContent = formatMoney(variant.compare_at_price, currency); el.style.display = ''; }
      else { el.style.display = 'none'; }
    });
    document.querySelectorAll('[data-tz-save]').forEach(function (el) {
      if (onSale) { el.textContent = '−' + pct + '%'; el.style.display = ''; }
      else { el.style.display = 'none'; }
    });

    // ATC label
    var atcLabel = form.querySelector('[data-tz-atc-label]');
    var atcBtn = form.querySelector('[data-tz-atc]');
    if (variant.available) {
      if (atcLabel) atcLabel.textContent = 'In den Warenkorb · ' + formatMoney(variant.price, currency);
      if (atcBtn) { atcBtn.disabled = false; atcBtn.classList.remove('is-disabled'); }
    } else {
      if (atcLabel) atcLabel.textContent = 'Ausverkauft';
      if (atcBtn) { atcBtn.disabled = true; atcBtn.classList.add('is-disabled'); }
    }

    // Sticky ATC
    var stickyPrice = document.querySelector('[data-tz-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, currency);

    // Option label e.g. "Sprache · *Englisch*"
    form.querySelectorAll('[data-tz-variant-group]').forEach(function (group) {
      var idx = parseInt(group.dataset.optionIndex, 10);
      var valEl = group.querySelector('[data-tz-variant-value="' + idx + '"]');
      if (valEl && variant.options[idx]) valEl.textContent = variant.options[idx];
    });

    // URL ?variant=<id>
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  function initVariantPickers(form) {
    form.querySelectorAll('[data-tz-variant-opt]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.classList.contains('is-soldout')) return;
        var idx = btn.dataset.optionIndex;
        var siblings = form.querySelectorAll('[data-tz-variant-opt][data-option-index="' + idx + '"]');
        siblings.forEach(function (s) { s.classList.remove('is-active'); });
        btn.classList.add('is-active');

        var values = getSelectedValues(form);
        var variant = findVariant(values);
        if (variant) applyVariant(form, variant);
      });
    });
  }

  /* ──────────────────────────────────────────────────────
     AJAX CART-ADD
     ────────────────────────────────────────────────────── */
  function ajaxCartAdd(form) {
    var fd = new FormData(form);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-tz-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count || '';
          el.hidden = !cart.item_count;
        });
        // Some themes also re-render the cart-drawer here; left to tz-shop.js.
        document.dispatchEvent(new CustomEvent('tz:cart-updated', { detail: cart }));
        return cart;
      });
  }

  function initBuyboxForm(form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var atc = form.querySelector('[data-tz-atc]');
      var label = atc && atc.querySelector('[data-tz-atc-label]');
      var originalLabel = label && label.textContent;
      if (atc) atc.disabled = true;
      if (label) label.textContent = 'Wird hinzugefügt …';

      ajaxCartAdd(form)
        .then(function () { return refreshCartCount(); })
        .then(function () {
          if (label) label.textContent = '✓ Im Warenkorb';
          // Open the cart-drawer if tz-shop.js exposes it
          document.dispatchEvent(new CustomEvent('tz:open-drawer', { detail: 'cart' }));
          setTimeout(function () {
            if (label && originalLabel) label.textContent = originalLabel;
            if (atc) atc.disabled = false;
          }, 1600);
        })
        .catch(function (err) {
          if (label) label.textContent = (err && err.description) || 'Fehler — nochmal versuchen';
          if (atc) atc.disabled = false;
          setTimeout(function () {
            if (label && originalLabel) label.textContent = originalLabel;
          }, 2400);
        });
    });
  }

  // Sticky-ATC button triggers the main form submit
  document.addEventListener('click', function (e) {
    var stickyBtn = e.target.closest && e.target.closest('[data-tz-pdp-sticky-btn]');
    if (!stickyBtn) return;
    e.preventDefault();
    var form = document.querySelector('[data-tz-pdp-form]');
    if (form) {
      // Use requestSubmit to keep submit handler
      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  });

  /* ──────────────────────────────────────────────────────
     MOBILE STICKY ATC — show after main ATC scrolls out
     ────────────────────────────────────────────────────── */
  function initStickyAtc() {
    var sticky = document.querySelector('[data-tz-pdp-sticky]');
    var trigger = document.querySelector('[data-tz-atc]');
    if (!sticky || !trigger || !window.IntersectionObserver) return;
    function toggleSticky(on) {
      sticky.classList.toggle('is-visible', on);
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('has-sticky-atc', on);
    }
    var io = new IntersectionObserver(function (entries) {
      var ent = entries[0];
      // Nur zeigen, wenn der Haupt-ATC NACH OBEN rausgescrollt ist (top < 0).
      // Solange er noch unter dem Fold liegt (noch nicht erreicht), bleibt sie aus.
      var scrolledPast = !ent.isIntersecting && ent.boundingClientRect.top < 0;
      toggleSticky(scrolledPast);
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
    io.observe(trigger);
  }

  /* ──────────────────────────────────────────────────────
     SHARE BUTTON — navigator.share + clipboard fallback
     ────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-tz-share]');
    if (!btn) return;
    e.preventDefault();
    var url = btn.dataset.shareUrl || window.location.href;
    var title = btn.dataset.shareTitle || document.title;
    var lbl = btn.querySelector('span');
    var originalLbl = lbl && lbl.textContent;

    function done(text) {
      if (lbl) {
        lbl.textContent = text;
        setTimeout(function () { if (lbl && originalLbl) lbl.textContent = originalLbl; }, 1600);
      }
    }
    if (navigator.share) {
      navigator.share({ title: title, url: url }).catch(function () {});
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () { done('Link kopiert'); });
    } else {
      done('—');
    }
  });

  /* ──────────────────────────────────────────────────────
     SMOOTH-SCROLL anchor links on the PDP only
     ────────────────────────────────────────────────────── */
  function initAnchorScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (a) {
      var href = a.getAttribute('href');
      if (href === '#') return;
      a.addEventListener('click', function (e) {
        var target = document.getElementById(href.slice(1));
        if (!target) return;
        e.preventDefault();
        var top = target.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }

  /* ──────────────────────────────────────────────────────
     INIT
     ────────────────────────────────────────────────────── */
  function init() {
    var gallery = document.querySelector('[data-pdp-gallery]');
    if (gallery) initGallery(gallery);

    var form = document.querySelector('[data-tz-pdp-form]');
    if (form) {
      initVariantPickers(form);
      initBuyboxForm(form);
    }

    initStickyAtc();
    initAnchorScroll();
  }

  // Override buybox submit to use tz-cart.js add-variant flow if available.
  // Keeps a clean separation: tz-pdp owns the form, tz-cart owns mutation.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('[data-tz-pdp-form]')) return;
    if (!window.TZCart || !window.TZCart.addVariant) return; // fall back to original handler
    e.preventDefault();
    var idInput = form.querySelector('[data-tz-variant-id]');
    if (!idInput) return;
    var atc = form.querySelector('[data-tz-atc]');
    var label = atc && atc.querySelector('[data-tz-atc-label]');
    var orig = label && label.textContent;
    if (atc) atc.disabled = true;
    if (label) label.textContent = 'Wird hinzugefügt …';
    window.TZCart.addVariant(idInput.value)
      .then(function () {
        if (label) label.textContent = '✓ Im Warenkorb';
        setTimeout(function () {
          if (label && orig) label.textContent = orig;
          if (atc) atc.disabled = false;
        }, 1600);
      })
      .catch(function () {
        if (label) label.textContent = 'Fehler — nochmal versuchen';
        if (atc) atc.disabled = false;
        setTimeout(function () { if (label && orig) label.textContent = orig; }, 2400);
      });
  }, true); // capture-phase to beat tz-pdp.js handler

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
